import { LayoutDashboard, Upload, Activity, LogOut, FileCheck, Brain, PenLine, Table2, Users, Shield, Settings, Building2, Plug, FolderTree, ClipboardList, Key, FileBarChart, ShieldCheck, LifeBuoy, UserCog } from 'lucide-react';
import { useMemo } from 'react';
import { Button } from '@/components/ui/button';

const ROLE_BADGE = {
  super_user: { label: 'Super User', cls: 'bg-purple-100 text-purple-800 border-purple-200' },
  admin: { label: 'Admin', cls: 'bg-red-50 text-red-700 border-red-200' },
  analyst: { label: 'Analyst', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  viewer: { label: 'Viewer', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
};

export const Sidebar = ({ activeTab, setActiveTab, onLogout, user }) => {
  const role = user?.role || 'viewer';
  const canWrite = ['super_user', 'admin', 'analyst'].includes(role);
  const isAdmin = ['super_user', 'admin'].includes(role);
  const isSuperUser = role === 'super_user';

  const visibleMenuItems = useMemo(() => (
    [
      { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, show: true },
      { id: 'upload', label: 'Upload Data', icon: Upload, show: canWrite },
      { id: 'results', label: 'Processing Results', icon: FileCheck, show: true },
      { id: 'processed-data', label: 'Processed Data', icon: Table2, show: true },
      { id: 'corrections', label: 'Corrections', icon: PenLine, show: canWrite },
      { id: 'suppliers', label: 'Suppl. Normalization', icon: Users, show: canWrite },
      { id: 'rules', label: 'Classification Rules', icon: Shield, show: canWrite },
      { id: 'connectors', label: 'Connectors', icon: Plug, show: canWrite },
      { id: 'taxonomy', label: 'Category Taxonomy', icon: FolderTree, show: canWrite },
      { id: 'enrichment', label: 'Supplier Profiles', icon: Building2, show: true },
      { id: 'reports', label: 'Reports', icon: FileBarChart, show: true },
      { id: 'ml', label: 'ML Performance', icon: Activity, show: true },
      { id: 'insights', label: 'ML Insights', icon: Brain, show: true },
      { id: 'super', label: 'Platform Admin', icon: UserCog, show: isSuperUser, highlight: true },
      { id: 'admin', label: 'User Management', icon: Settings, show: isAdmin },
      { id: 'audit', label: 'Audit Log', icon: ClipboardList, show: isAdmin },
      { id: 'apikeys', label: 'API Keys', icon: Key, show: isAdmin || role === 'analyst' },
      { id: 'privacy', label: 'Privacy & Data', icon: ShieldCheck, show: true },
      { id: 'help', label: 'Help', icon: LifeBuoy, show: true },
    ].filter(m => m.show)
  ), [role, canWrite, isAdmin, isSuperUser]);

  const badge = ROLE_BADGE[role] || ROLE_BADGE.viewer;

  return (
    <div className="w-64 bg-white border-r border-slate-200 h-screen flex flex-col">
      <div className="p-6 border-b border-slate-200">
        <img 
          src="https://customer-assets.emergentagent.com/job_smart-spend-classify/artifacts/55ti7wnc_ProcureAide%20Logo%20with%20Ai.png"
          alt="ProcureAide"
          className="w-full h-auto"
          style={{ maxWidth: '180px' }}
        />
      </div>
      
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {visibleMenuItems.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              data-testid={`nav-${item.id}-button`}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-medium transition-all duration-200 ${
                activeTab === item.id
                  ? 'bg-[#0F172A] text-white'
                  : item.highlight
                    ? 'text-purple-700 hover:bg-purple-50 border border-purple-100'
                    : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              <Icon size={18} className="flex-shrink-0" />
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>
      
      <div className="p-4 border-t border-slate-200">
        <div className="mb-3 px-2">
          {user?.organization_name && (
            <p className="text-xs font-semibold text-[#0F172A] mb-1 truncate" data-testid="sidebar-org-name">{user.organization_name}</p>
          )}
          <p className="text-sm font-medium text-slate-700 truncate">{user?.full_name || user?.email}</p>
          <div className="flex items-center gap-2 mt-1">
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold border ${badge.cls}`} data-testid="user-role-badge">
              {badge.label}
            </span>
            <span className="text-xs text-slate-400 truncate">{user?.email}</span>
          </div>
        </div>
        <Button
          data-testid="logout-button"
          onClick={onLogout}
          variant="outline"
          className="w-full justify-start gap-2 text-slate-600"
        >
          <LogOut size={16} />
          <span>Logout</span>
        </Button>
      </div>
    </div>
  );
};
